import { aiImg2ImgPromptCaseAPI } from '@/utils/api';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  normalizeTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  buildText2ImgCaseOwnerFilterOptions,
  resolveText2ImgCaseOwnerLabel,
} from '@/pages/ipe/workbench/utils/text2imgPromptCaseLoader';

export const IMG2IMG_CASE_DEFAULT_PAGE_SIZE = 20;
export const IMG2IMG_CASE_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

/** 功能页空态试玩位：官方模板封面条最多条数 */
export const IMG2IMG_TRY_OFFICIAL_FETCH_SIZE = 20;

/** 将后端 CaseView 转为改图模板库统一结构（fallbackScope：后端未返回 caseScope 时的兜底） */
export function mapImg2ImgPromptCaseApiItem(item, fallbackScope = 'official') {
  if (!item || typeof item !== 'object') return null;
  const prompt = String(item.prompt || '').trim();
  const name = String(item.name || '').trim() || '未命名模板';
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  return {
    id,
    name,
    prompt: prompt || name,
    coverUrl: String(item.coverUrl || item.url || '').trim(),
    scope: normalizeTitlePresetScope(item.caseScope || item.scope || fallbackScope),
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    ownerDisplayName: String(item.ownerDisplayName || '').trim(),
    sortWeight: Number(item.sortWeight) || 0,
    source: 'api',
  };
}

/**
 * 按 scope 真分页拉取万能改图模板（个人 / 团队 / 官方）。
 * includeOfficial=true 时后端会把「官方」合并进本次查询（前端模板库锁定作用域时使用）。
 */
export async function loadImg2ImgPromptCasesPage({
  scope = DEFAULT_TITLE_PRESET_SCOPE,
  keyword = '',
  ownerUserId = '',
  page = 1,
  pageSize = IMG2IMG_CASE_DEFAULT_PAGE_SIZE,
  includeOfficial = false,
} = {}) {
  const safeScope = normalizeTitlePresetScope(scope);
  const ownerId = ownerUserId != null && String(ownerUserId).trim() !== ''
    ? String(ownerUserId).trim()
    : undefined;
  const res = await aiImg2ImgPromptCaseAPI.list({
    scope: safeScope,
    keyword: String(keyword || '').trim() || undefined,
    ownerUserId: safeScope === 'team' ? ownerId : undefined,
    page,
    pageSize,
    includeOfficial: includeOfficial ? true : undefined,
  });
  const items = (Array.isArray(res?.items) ? res.items : [])
    .map((raw) => mapImg2ImgPromptCaseApiItem(raw, safeScope))
    .filter(Boolean);
  return {
    items,
    total: Number(res?.total) || 0,
    page: Number(res?.page) || page || 1,
    pageSize: Number(res?.pageSize) || pageSize || IMG2IMG_CASE_DEFAULT_PAGE_SIZE,
    caseScope: res?.caseScope || safeScope,
  };
}

/**
 * 拉取官方模板中带封面的条目，供空态「或者用案例试试」展示（仅图片）。
 */
export async function loadImg2ImgOfficialTryCases(limit = 3) {
  const safeLimit = Math.max(1, Number(limit) || 3);
  const res = await loadImg2ImgPromptCasesPage({
    scope: 'official',
    page: 1,
    pageSize: Math.max(safeLimit, IMG2IMG_TRY_OFFICIAL_FETCH_SIZE),
  });
  return (Array.isArray(res.items) ? res.items : [])
    .filter((item) => String(item?.coverUrl || '').trim())
    .slice(0, safeLimit);
}

export const resolveImg2ImgCaseOwnerLabel = resolveText2ImgCaseOwnerLabel;
export const buildImg2ImgCaseOwnerFilterOptions = buildText2ImgCaseOwnerFilterOptions;

export { resolveVisibleTitlePresetScopes, DEFAULT_TITLE_PRESET_SCOPE };

/** 工作流执行：优先用 params.prompt，否则按模板 ID 拉取指令内容 */
export async function resolveImg2ImgWorkflowPrompt(params = {}) {
  let prompt = String(params.prompt || '').trim();
  if (prompt) return prompt;
  const caseId = String(params.img2imgPromptCaseId || '').trim();
  if (!caseId) return '';
  try {
    const res = await aiImg2ImgPromptCaseAPI.getById(caseId);
    const mapped = mapImg2ImgPromptCaseApiItem(res?.case);
    return String(mapped?.prompt || '').trim();
  } catch {
    return '';
  }
}
